import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@shared/lib/supabase';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export interface Invoice {
  id: string;
  merchant_id: string;
  number: string;
  recipient: string;
  amount: number;
  description: string;
  notes: string;
  due_date: string | null;
  status: InvoiceStatus;
  solution: string;
  payment_link: string | null;
  created_at: string;
  updated_at: string;
}

export default function useStripeInvoices(userId: string | undefined) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('invoices')
        .select('*')
        .eq('solution', 'stripe')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setInvoices((data || []) as Invoice[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return invoices;
    if (statusFilter === 'overdue') {
      return invoices.filter(
        (inv) => inv.status === 'sent' && inv.due_date && new Date(inv.due_date) < new Date(),
      );
    }
    return invoices.filter((inv) => inv.status === statusFilter);
  }, [invoices, statusFilter]);

  async function createInvoice(data: {
    recipient: string;
    amount: number;
    description: string;
    notes: string;
    due_date: string | null;
    status: InvoiceStatus;
  }) {
    if (!userId) throw new Error('Not authenticated');

    const { count } = await supabase
      .from('invoices')
      .select('id', { count: 'exact', head: true })
      .eq('merchant_id', userId);
    const num = (count || 0) + 1001;

    const newInvoice = {
      merchant_id: userId,
      number: `INV-${num}`,
      recipient: data.recipient,
      amount: data.amount,
      description: data.description,
      notes: data.notes,
      due_date: data.due_date || null,
      status: data.status,
      solution: 'stripe',
      payment_link: null,
    };

    const { data: created, error: err } = await supabase
      .from('invoices')
      .insert(newInvoice)
      .select()
      .maybeSingle();

    if (err) throw err;
    if (created) {
      setInvoices((prev) => [created as Invoice, ...prev]);
    }
    return created as Invoice;
  }

  async function updateStatus(id: string, status: InvoiceStatus) {
    const { error: err } = await supabase.from('invoices').update({ status }).eq('id', id);
    if (err) throw err;
    setInvoices((prev) => prev.map((inv) => (inv.id === id ? { ...inv, status } : inv)));
  }

  return {
    invoices,
    filtered,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    createInvoice,
    updateStatus,
    refetch: load,
  };
}
